import { useState, useEffect } from 'react';
import {
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Divider,
  CircularProgress,
  IconButton,
  Modal,
  Box,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

import { ActionItem } from '@urbanmix-tech/shared-js';
import { useAuth } from '@/shared/context/AuthProvider';
import { useSignedUrls } from '@/shared/hooks/useSignedUrls';
import { TaskImage } from './InspectScopeContent';
import { SCOPE_CATEGORIES } from './ActionItemModal/ActionItemModal';

const PRIORITIES = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
];

export interface ReviewWorkResponse {
  paymentPriority?: string;
  paymentAssignee?: string;
  paymentAssigneeEmail?: string;
  paymentTargetDate?: string | null;
  paymentNotes?: string;
  category?: string;  // SCOPE category (e.g., 'windows', 'plumbing') passed through from inspect stage
}

interface ReviewWorkContentProps {
  actionItem: ActionItem;
  onChange?: (data: ReviewWorkResponse) => void;
}

export function ReviewWorkContent({ actionItem, onChange }: ReviewWorkContentProps) {
  const { usersData } = useAuth();
  const allUsers = Object.values(usersData);

  const existingResponse = actionItem.response || {};

  // Read-only data from previous stages
  const beforeImages: TaskImage[] = existingResponse.images || [];
  const afterImages: TaskImage[] = existingResponse.afterImages || [];
  const allImages = [...beforeImages, ...afterImages];
  const signedUrls = useSignedUrls(allImages);

  // UI state for image preview
  const [selectedImage, setSelectedImage] = useState<TaskImage | null>(null);

  // Editable form state for payment assignment (only NEW fields this stage adds)
  const [priority, setPriority] = useState(existingResponse.paymentPriority || 'medium');
  const [assignedTo, setAssignedTo] = useState(existingResponse.paymentAssignee || '');
  const [targetDate, setTargetDate] = useState<string>(
    existingResponse.paymentTargetDate ? new Date(existingResponse.paymentTargetDate).toISOString().split('T')[0] : ''
  );
  const [notes, setNotes] = useState(existingResponse.paymentNotes || '');

  // Report changes to parent (only NEW fields this stage adds + category passthrough)
  useEffect(() => {
    if (onChange) {
      const selectedUser = assignedTo ? usersData[assignedTo] : null;
      onChange({
        paymentPriority: priority,
        paymentAssignee: assignedTo,
        paymentAssigneeEmail: selectedUser?.email || '',
        paymentTargetDate: targetDate ? new Date(targetDate).toISOString() : null,
        paymentNotes: notes,
        category: existingResponse.category,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priority, assignedTo, targetDate, notes]);

  // Get user name from ID
  const getUserName = (userId: string) => {
    if (!userId) return 'Unassigned';
    const user = usersData[userId];
    return user?.fullName || user?.email || 'Unknown';
  };

  // Get category label from value
  const getCategoryLabel = (value: string) => {
    const cat = SCOPE_CATEGORIES.find((c) => c.value === value);
    return cat?.label || value || 'Not specified';
  };

  // Full-screen image overlay
  if (selectedImage) {
    const displayUrl = signedUrls[selectedImage.id] || selectedImage.url;
    return (
      <Modal open onClose={() => setSelectedImage(null)}>
        <Box
          className="absolute left-1/2 top-1/2 h-[90%] w-[90%] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-primary bg-black p-4"
          onClick={() => setSelectedImage(null)}
        >
          <IconButton
            onClick={() => setSelectedImage(null)}
            className="absolute right-4 top-4 z-10 text-white"
          >
            <ClearIcon fontSize="large" />
          </IconButton>
          <img
            src={displayUrl}
            alt=""
            className="h-full w-full object-contain"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </Box>
      </Modal>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Read-only section: Data from Inspect + Work phases */}
      <div className="rounded-lg bg-gray-800/50 p-12">
        {/* <Typography variant="subtitle2" className="mb-3 text-stone-300">
          Work Summary
        </Typography> */}

        {/* Before | After thumbnails side by side */}
        {(beforeImages.length > 0 || afterImages.length > 0) && (
          <div className="mb-4 flex gap-4">
            {/* Before thumbnail */}
            <div className="flex-1">
              <Typography variant="caption" className="mb-2 block text-stone-300">
                Before
              </Typography>
              {beforeImages[0] ? (
                <div>
                  {signedUrls[beforeImages[0].id] ? (
                    <img
                      src={signedUrls[beforeImages[0].id]}
                      alt=""
                      className="h-[140px] w-[140px] cursor-pointer rounded object-cover hover:opacity-80"
                      onClick={() => setSelectedImage(beforeImages[0])}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="flex h-[140px] w-[140px] items-center justify-center rounded bg-gray-700">
                      <CircularProgress size={16} />
                    </div>
                  )}
                  {beforeImages[0].note && (
                    <Typography variant="caption" className="mt-1 block truncate text-stone-500">
                      {beforeImages[0].note}
                    </Typography>
                  )}
                </div>
              ) : (
                <div className="flex h-[140px] w-[140px] items-center justify-center rounded bg-gray-700 text-gray-500">
                  -
                </div>
              )}
            </div>

            {/* After thumbnail */}
            <div className="flex-1">
              <Typography variant="caption" className="mb-2 block text-stone-300">
                After
              </Typography>
              {afterImages[0] ? (
                <div>
                  {signedUrls[afterImages[0].id] ? (
                    <img
                      src={signedUrls[afterImages[0].id]}
                      alt=""
                      className="h-[140px] w-[140px] cursor-pointer rounded object-cover hover:opacity-80"
                      onClick={() => setSelectedImage(afterImages[0])}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="flex h-[140px] w-[140px] items-center justify-center rounded bg-gray-700">
                      <CircularProgress size={16} />
                    </div>
                  )}
                  {afterImages[0].note && (
                    <Typography variant="caption" className="mt-1 block truncate text-stone-500">
                      {afterImages[0].note}
                    </Typography>
                  )}
                </div>
              ) : (
                <div className="flex h-[140px] w-[140px] items-center justify-center rounded bg-gray-700 text-gray-500">
                  -
                </div>
              )}
            </div>
          </div>
        )}

        {/* Read-only info grid */}
        {/* <Stack spacing={0.5} className="grid grid-cols-2 cursor-default text-sm">
          <div className="flex gap-4">
            <Typography variant="body2" className="text-stone-300">
              Category:
            </Typography>
            <Typography variant="body2" className="text-white">
              {getCategoryLabel(existingResponse.category)}
            </Typography>
          </div>
          <div className="flex gap-4">
            <Typography variant="body2" className="text-stone-300">
              Assigned To:
            </Typography>
            <Typography variant="body2" className="text-white">
              {getUserName(existingResponse.assignedTo)}
            </Typography>
          </div>
          <div className="flex gap-4">
            <Typography variant="body2" className="text-stone-300">
              Priority:
            </Typography>
            <Typography variant="body2" className="capitalize text-white">
              {existingResponse.priority || 'Medium'}
            </Typography>
          </div>
          <div className="flex gap-4">
            <Typography variant="body2" className="text-stone-300">
              Target Date:
            </Typography>
            <Typography variant="body2" className="text-white">
              {existingResponse.targetDate
                ? new Date(existingResponse.targetDate).toLocaleDateString()
                : '-'}
            </Typography>
          </div>
          <div className="flex gap-4">
            <Typography variant="body2" className="text-stone-300">
              Completion Date:
            </Typography>
            <Typography variant="body2" className="text-white">
              {existingResponse.completionDate
                ? new Date(existingResponse.completionDate).toLocaleDateString()
                : actionItem.completedOn
                  ? new Date(actionItem.completedOn as any).toLocaleDateString()
                  : '-'}
            </Typography>
          </div>
        </Stack> */}

        {/* Notes from previous stages */}
        {existingResponse.notes && (
          <div className="mt-3">
            <Typography variant="body2" className="text-stone-300">
              Inspector Notes:
            </Typography>
            <Typography variant="body2" className="mt-1 text-white">
              {existingResponse.notes}
            </Typography>
          </div>
        )}
        {existingResponse.additionalNotes && (
          <div className="mt-3">
            <Typography variant="body2" className="text-stone-300">
              Additional Notes:
            </Typography>
            <Typography variant="body2" className="mt-1 text-white">
              {existingResponse.additionalNotes}
            </Typography>
          </div>
        )}
      </div>

      <Divider className="!border-gray-700" />

      {/* Editable section: Payment Assignment Form */}
      <Typography variant="subtitle2" className="text-stone-300">
        Payment Assignment
      </Typography>

      <FormControl fullWidth variant="outlined" size="small">
        <InputLabel>Assignee</InputLabel>
        <Select
          value={assignedTo}
          onChange={(e) => setAssignedTo(e.target.value)}
          label="Assignee"
        >
          <MenuItem value="">
            <em>Unassigned</em>
          </MenuItem>
          {allUsers.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {user.fullName || user.email}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth variant="outlined" size="small">
        <InputLabel>Priority</InputLabel>
        <Select
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          label="Priority"
        >
          {PRIORITIES.map((p) => (
            <MenuItem key={p.value} value={p.value}>
              {p.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Target Date"
        type="date"
        value={targetDate}
        onChange={(e) => setTargetDate(e.target.value)}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Notes"
        multiline
        rows={3}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        fullWidth
        size="small"
        placeholder="Additional notes for payment processing..."
      />
    </Stack>
  );
}
